export const HIGHLIGHT_URL_ID_CAP = 100;

/** Parse the `highlight` query param into a de-duplicated list of file ids. */
export function parseHighlightIds(raw: string | null | undefined): string[] {
  if (!raw) return [];
  const ids = raw.split(',').map((s) => s.trim()).filter(Boolean);
  return Array.from(new Set(ids));
}

/** Build `/file-storage?highlight=a,b,...` (capped). Returns the bare path when no ids. */
export function buildFileStorageHighlightUrl(ids: string[], cap: number = HIGHLIGHT_URL_ID_CAP): string {
  const unique = Array.from(new Set(ids.map((s) => s.trim()).filter(Boolean)));
  if (unique.length === 0) return '/file-storage';
  return `/file-storage?highlight=${encodeURIComponent(unique.slice(0, cap).join(','))}`;
}

/** Ancestor folder paths for a file's folder (e.g. "A/B" -> ["A", "A/B"]). */
export function folderAncestorPaths(folder: string | null | undefined): string[] {
  const parts = (folder || 'Other').split('/');
  const paths: string[] = [];
  let acc = '';
  for (const p of parts) {
    acc = acc ? `${acc}/${p}` : p;
    paths.push(acc);
  }
  return paths;
}

export const BATCH_HIGHLIGHT_KEY = 'batchHighlightFileIds';

export interface ImportResultLike {
  needs_direction_review?: boolean;
  company_not_detected?: boolean;
  needs_review?: boolean;
  parse_failed?: boolean;
  empty_transactions?: boolean;
}

/** True when the OCR result carries any review flag (mirrors FileUpload.tsx's needsReview). */
export function needsReview(result: ImportResultLike | null | undefined): boolean {
  return !!(
    result?.needs_direction_review ||
    result?.company_not_detected ||
    result?.needs_review ||
    result?.parse_failed ||
    result?.empty_transactions
  );
}

export interface QueueEntry {
  docType: string;
  reviewId: string;
  filename: string;
  flags?: string;
}

/** Route for a queued review item. */
export function reviewItemUrl(entry: QueueEntry): string {
  if (entry.docType === 'bank_statement') return `/bank-statements/review/${entry.reviewId}`;
  if (entry.docType === 'card_statement') return `/card-statements/review/${entry.reviewId}`;
  return `/invoices/review/${entry.reviewId}${entry.flags || ''}`;
}

/** Persist the ids to highlight once the review queue is cleared. */
export function stashBatchHighlightIds(ids: string[]): void {
  try {
    sessionStorage.setItem(BATCH_HIGHLIGHT_KEY, JSON.stringify(ids));
  } catch { /* sessionStorage unavailable */ }
}

function readBatchHighlightIds(): string[] {
  try {
    const raw = sessionStorage.getItem(BATCH_HIGHLIGHT_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((x): x is string => typeof x === 'string') : [];
  } catch {
    return [];
  }
}

/** Read the stashed ids without clearing them. */
export function peekBatchHighlightIds(): string[] {
  return readBatchHighlightIds();
}

/** Read and clear the stashed ids. */
export function takeBatchHighlightIds(): string[] {
  const ids = readBatchHighlightIds();
  try { sessionStorage.removeItem(BATCH_HIGHLIGHT_KEY); } catch { /* ignore */ }
  return ids;
}

/**
 * Decide where an upload flow goes after processing.
 * - queued === 0 -> navigate straight to the highlighted files.
 * - queued  > 0 -> review first: stash the ids for the queue-completion handoff.
 */
export function postUploadPlan(importedFileIds: string[], queued: number): { navigate?: string; stash: string[] } {
  if (queued > 0) return { stash: importedFileIds };
  return { navigate: buildFileStorageHighlightUrl(importedFileIds), stash: [] };
}

/**
 * Plan what happens when a batch upload finishes.
 * - queued  > 0 -> mixed batch: stash the ids for the review-queue handoff.
 * - queued == 0 -> all-clean: navigate straight to the highlighted files, but only
 *   while the user is still on File Storage (never yank them back).
 */
export function postBatchPlan(
  fileIds: string[],
  queued: number,
  onFileStorage: boolean,
): { navigate?: string; stash?: string[] } {
  if (queued > 0) return { stash: fileIds };
  if (fileIds.length > 0 && onFileStorage) return { navigate: buildFileStorageHighlightUrl(fileIds) };
  return {};
}

export interface QueueHandoff {
  nextUrl?: string;
  highlightUrl?: string;
}

/**
 * Advance the review queue after confirming/discarding the current item and decide
 * where to go next.
 * - queue still has a next item -> persist the shortened queue, return its review URL,
 *   and leave the batch-highlight stash for later.
 * - queue absent or just drained -> clear the queue keys, consume the stashed batch ids
 *   (even when the queue was cleared elsewhere), and return the File Storage highlight URL.
 *
 * Returns null when there is nowhere to go.
 */
export function advanceReviewQueue(): QueueHandoff | null {
  let raw: string | null = null;
  try { raw = sessionStorage.getItem('reviewQueue'); } catch { /* sessionStorage unavailable */ }
  if (raw) {
    try {
      const queue = JSON.parse(raw) as QueueEntry[];
      if (Array.isArray(queue)) {
        if (queue.length > 0) queue.shift();
        if (queue.length > 0) {
          sessionStorage.setItem('reviewQueue', JSON.stringify(queue));
          return { nextUrl: reviewItemUrl(queue[0]) };
        }
      }
    } catch { /* malformed queue -> fall through and drain */ }
  }
  sessionStorage.removeItem('reviewQueue');
  sessionStorage.removeItem('reviewQueueTotal');
  const batch = takeBatchHighlightIds();
  if (batch.length > 0) return { highlightUrl: buildFileStorageHighlightUrl(batch) };
  return null;
}
